import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import { useMemo } from 'react';
import Gallery from 'react-image-gallery';
import ImageComponent from '#app/components/common/image-component';
import {
  Carousel,
  CarouselContent,
  CarouselItem } from
'#app/components/ui/carousel';

// Types
import { type StyleVariant } from '#types/product';

// Styles for react-image-gallery
import "react-image-gallery/styles/css/image-gallery.css?__remix_sideEffect__";
import "./gallery-styles.css?__remix_sideEffect__";

interface ProductGalleryProps {
  activeStyle: StyleVariant;
  /**
   * An array of images, each containing an `original` and `thumbnail` URL.
   * Adjust as needed based on your data shape.
   */
  images: Array<{original: string;thumbnail: string;}>;
}

const ProductGallery = ({ activeStyle, images }: ProductGalleryProps) => {
  const ImageGallery = (Gallery as any).default ?? Gallery;

  // Fallback images if none provided in the `images` prop
  const transformedVariantImages =
  activeStyle.variants[0]?.images?.map((img) => ({
    url: img.url
  })) || [];

  // Decide which array of images to use
  const galleryImages =
  images.length > 0 ?
  images.map((img) => ({ url: img.original })) :
  transformedVariantImages;

  // Memoize the array of image objects in the shape needed by react-image-gallery
  const imagesMemo = useMemo(() => {
    return galleryImages.map((productImage: {url: string;}) => {
      return {
        original: productImage.url,
        thumbnail: productImage.url,

        // This controls how the main image is rendered in react-image-gallery
        renderItem: () => {
          return (
            <>
							{
              <ImageComponent
                src={productImage.url}
                key={productImage.url}
                size="original"
                className="max-h-[575px] w-[431px] cursor-default rounded-xl "
                alt="Product"
                loading="eager"
                fetchpriority="high" />}


						</>);

        },

        // This controls how each thumbnail is rendered in react-image-gallery
        renderThumbInner: () => {
          return (
            <ImageComponent
              className="w-[95px] cursor-pointer rounded-xl"
              size="thumbnail"
              fetchpriority="high"
              src={productImage.url}
              key={productImage.url}
              loading="eager"
              alt="thumbnail" />);


        }
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [galleryImages, activeStyle]);

  return (
    <div className="grow-0" key={activeStyle.color}>
			<div
        className="sticky h-auto"
        style={{
          top: '180px'
        }}>

				{/* Desktop gallery - hidden on small screens, shown on md screens and up */}
				<div className="hidden md:block">
					<ImageGallery
            items={imagesMemo}
            thumbnailPosition="left"
            showPlayButton={false}
            showFullscreenButton={false}
            showNav={false}
            slideOnThumbnailOver={true}
            showThumbnails={true}
            disableThumbnailScroll={false}
            slideDuration={10}
            infinite={false} />

				</div>

				{/* Mobile carousel - shown on small screens, hidden on md screens and up */}
				<Carousel
          className="relative w-full md:hidden"
          plugins={[WheelGesturesPlugin()]}
          opts={{
            dragFree: true,
            loop: false,
            align: 'start',
            skipSnaps: true
          }}>

					<CarouselContent className="w-full" disableOrientation>
						{imagesMemo.map((image, index) =>
            <CarouselItem
              key={image.original}
              className="relative mb-[-22px] max-w-[332px] basis-[90%] pl-0"
              disableOrientation>

								<ImageComponent
                src={image.original}
                key={image.original}
                size="original"
                className="w-auto cursor-default overflow-hidden"
                alt={`Product Image ${index + 1}`}
                loading="eager"
                fetchpriority="high" />

							</CarouselItem>
            )}
					</CarouselContent>
				</Carousel>
			</div>
		</div>);

};

export default ProductGallery;